import * as THREE from 'three';

/** One B-rep face's slice of `triangles`, in index-array offsets. */
export interface MeshFaceGroup {
  readonly start: number;
  readonly count: number;
}

/** Triangles a CAD kernel already made, as replicad's `shape.mesh()` returns them. */
export interface MeshData {
  readonly vertices: ArrayLike<number>;
  readonly triangles: ArrayLike<number>;
  readonly normals?: ArrayLike<number>;
  readonly faceGroups?: ArrayLike<MeshFaceGroup>;
}

const HEADER_WORDS = 4;
const HEADER_BYTES = 4 * HEADER_WORDS;

/** The viewer takes every model as a Blob, so a mesh travels packed into one for the `mesh` loader. */
export function meshBlob(mesh: MeshData): Blob {
  const normals = mesh.normals?.length === mesh.vertices.length ? mesh.normals : [];
  const groups = Array.from(mesh.faceGroups ?? [], ({ start, count }) => [start, count]).flat();
  const header = new Uint32Array([mesh.vertices.length, mesh.triangles.length, normals.length, groups.length]);
  return new Blob([
    header,
    Float32Array.from(mesh.vertices),
    Uint32Array.from(mesh.triangles),
    Float32Array.from(normals),
    Uint32Array.from(groups),
  ]);
}

function coversIndex(groups: Uint32Array, indexCount: number): boolean {
  let end = 0;
  for (let i = 0; i < groups.length; i += 2) {
    const count = groups[i + 1];
    if (groups[i] !== end || count === 0 || count % 3 !== 0) return false;
    end += count;
  }
  return groups.length > 0 && end === indexCount;
}

/** The mesh as one geometry. Its `groups` are the B-rep faces, set only when the kernel's groups tile the index. */
export function meshGeometry(buffer: ArrayBuffer): THREE.BufferGeometry {
  if (buffer.byteLength < HEADER_BYTES) throw new Error(`Invalid mesh data (${buffer.byteLength} bytes).`);
  const [vertexCount, indexCount, normalCount, groupWords] = new Uint32Array(buffer, 0, HEADER_WORDS);
  const expected = HEADER_BYTES + 4 * (vertexCount + indexCount + normalCount + groupWords);
  if (
    buffer.byteLength !== expected ||
    vertexCount % 3 !== 0 ||
    indexCount % 3 !== 0 ||
    groupWords % 2 !== 0 ||
    (normalCount !== 0 && normalCount !== vertexCount)
  ) {
    throw new Error(`Invalid mesh data (${buffer.byteLength} bytes, expected ${expected}).`);
  }
  const positions = new Float32Array(buffer, HEADER_BYTES, vertexCount);
  const index = new Uint32Array(buffer, HEADER_BYTES + 4 * vertexCount, indexCount);
  const points = vertexCount / 3;
  if (index.some((vertex) => vertex >= points)) throw new Error('Invalid mesh data: a triangle names a missing vertex.');

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(new THREE.BufferAttribute(index, 1));
  if (normalCount > 0) {
    const normals = new Float32Array(buffer, HEADER_BYTES + 4 * (vertexCount + indexCount), normalCount);
    geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  } else {
    geometry.computeVertexNormals();
  }
  const groups = new Uint32Array(buffer, HEADER_BYTES + 4 * (vertexCount + indexCount + normalCount), groupWords);
  if (coversIndex(groups, indexCount)) {
    for (let i = 0; i < groups.length; i += 2) geometry.addGroup(groups[i], groups[i + 1]);
  }
  return geometry;
}

// Each face keeps only its own vertices, so its bounds and boundary are the face's alone.
function faceGeometry(geometry: THREE.BufferGeometry, group: { start: number; count: number }): THREE.BufferGeometry {
  const index = geometry.index as THREE.BufferAttribute;
  const position = geometry.getAttribute('position').array as Float32Array;
  const normal = geometry.getAttribute('normal').array as Float32Array;
  const local = new Map<number, number>();
  const faceIndex = new Uint32Array(group.count);
  for (let i = 0; i < group.count; i++) {
    const vertex = index.getX(group.start + i);
    let at = local.get(vertex);
    if (at === undefined) {
      at = local.size;
      local.set(vertex, at);
    }
    faceIndex[i] = at;
  }
  const positions = new Float32Array(3 * local.size);
  const normals = new Float32Array(3 * local.size);
  local.forEach((at, vertex) => {
    positions.set(position.subarray(3 * vertex, 3 * vertex + 3), 3 * at);
    normals.set(normal.subarray(3 * vertex, 3 * vertex + 3), 3 * at);
  });
  const face = new THREE.BufferGeometry();
  face.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  face.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  face.setIndex(new THREE.BufferAttribute(faceIndex, 1));
  return face;
}

export function faceGeometries(geometry: THREE.BufferGeometry): THREE.BufferGeometry[] {
  return geometry.groups.map((group) => faceGeometry(geometry, group));
}
